import { Box } from "@mui/material"
import { keyframes } from "@mui/material/styles"

export type FeedbackStatus = 'loading' | 'x' | 'alerta' | 'sucesso'

const CIRCUNFERENCIA = 283
const TRACO_X = 43
const TRACO_ALERTA = 26
const TRACO_CHECK = 56

const girar = keyframes`
  to { transform: rotate(360deg); }
`

const pulsarArco = keyframes`
  0%   { stroke-dasharray: 20 ${CIRCUNFERENCIA}; stroke-dashoffset: 0; }
  50%  { stroke-dasharray: 170 ${CIRCUNFERENCIA}; stroke-dashoffset: -40; }
  100% { stroke-dasharray: 20 ${CIRCUNFERENCIA}; stroke-dashoffset: -${CIRCUNFERENCIA}; }
`

const fecharAnel = keyframes`
  from { stroke-dashoffset: ${CIRCUNFERENCIA}; }
  to   { stroke-dashoffset: 0; }
`

const desenharX = keyframes`
  from { stroke-dashoffset: ${TRACO_X}; }
  to   { stroke-dashoffset: 0; }
`

const desenharAlerta = keyframes`
  from { stroke-dashoffset: ${TRACO_ALERTA}; }
  to   { stroke-dashoffset: 0; }
`

const desenharCheck = keyframes`
  from { stroke-dashoffset: ${TRACO_CHECK}; }
  to   { stroke-dashoffset: 0; }
`

const surgir = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`

const DUR_ANEL = 0.45
const DUR_TRACO = 0.2

const tracoBase = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 7,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
} as const

export default function StatusRing({ status, tamanho = 170, }: { status: FeedbackStatus; tamanho?: number }): JSX.Element {
    const carregando = status === 'loading'

    return (
        <Box
            component="svg"
            viewBox="0 0 100 100"
            sx={{ width: tamanho, height: tamanho, color: status === 'sucesso' ? 'success.main' : 'primary.main' }}
        >
            {!carregando && (
                <Box
                    component="circle"
                    cx={50}
                    cy={50}
                    r={45}
                    sx={{
                        fill: 'currentColor',
                        fillOpacity: 0.12,
                        animation: `${surgir} ${DUR_ANEL}s ease-out both`,
                    }}
                />
            )}

            <Box
                component="circle"
                cx={50}
                cy={50}
                r={45}
                sx={{
                    ...tracoBase,
                    ...(carregando
                        ? {
                            transformBox: 'fill-box',
                            transformOrigin: '50% 50%',
                            animation: `${girar} 1.6s linear infinite, ${pulsarArco} 1.4s ease-in-out infinite`,
                        }
                        : {
                            transformBox: 'fill-box',
                            transformOrigin: '50% 50%',
                            transform: 'rotate(-90deg)',
                            strokeDasharray: CIRCUNFERENCIA,
                            animation: `${fecharAnel} ${DUR_ANEL}s cubic-bezier(0.16, 1, 0.3, 1) both`,
                        }),
                }}
            />

            {status === 'x' && (
                <>
                    <Box
                        component="line"
                        x1={35}
                        y1={35}
                        x2={65}
                        y2={65}
                        sx={{
                            ...tracoBase,
                            strokeDasharray: TRACO_X,
                            animation: `${desenharX} ${DUR_TRACO}s ease-out ${DUR_ANEL}s both`,
                        }}
                    />

                    <Box
                        component="line"
                        x1={65}
                        y1={35}
                        x2={35}
                        y2={65}
                        sx={{
                            ...tracoBase,
                            strokeDasharray: TRACO_X,
                            animation: `${desenharX} ${DUR_TRACO}s ease-out ${DUR_ANEL + DUR_TRACO}s both`,
                        }}
                    />
                </>
            )}

            {status === 'alerta' && (
                <>
                    <Box
                        component="line"
                        x1={50}
                        y1={30}
                        x2={50}
                        y2={56}
                        sx={{
                            ...tracoBase,
                            strokeDasharray: TRACO_ALERTA,
                            animation: `${desenharAlerta} ${DUR_TRACO}s ease-out ${DUR_ANEL}s both`,
                        }}
                    />

                    <Box
                        component="circle"
                        cx={50}
                        cy={68}
                        r={3.5}
                        sx={{
                            fill: 'currentColor',
                            animation: `${surgir} ${DUR_TRACO}s ease-out ${DUR_ANEL + DUR_TRACO}s both`,
                        }}
                    />
                </>
            )}

            {status === 'sucesso' && (
                <Box
                    component="path"
                    d="M32 52 L45 65 L70 38"
                    sx={{
                        ...tracoBase,
                        strokeDasharray: TRACO_CHECK,
                        animation: `${desenharCheck} ${DUR_TRACO * 1.5}s ease-out ${DUR_ANEL}s both`,
                    }}
                />
            )}
        </Box>
    )
}